import { useEffect, useState } from "react";
import { ChevronDown, Menu, X } from "lucide-react";

/**
 * MobileMenu – hamburger button + full-screen slide-in drawer.
 * Mirrors the desktop `Sidebar` API (`items`, `active`, `onSelect`), so we can pass
 * the exact same collapsible tree structure. All tap targets ≥ 44px per WCAG.
 */
export const MobileMenu = ({ items, active, onSelect }) => {
  const [openDrawer, setOpenDrawer] = useState(false);
  const [open, setOpen] = useState({});

  // Auto-open the group containing the active tab (walks recursively)
  useEffect(() => {
    setOpen((prev) => {
      const next = { ...prev };
      const walk = (list) => {
        for (const it of list || []) {
          if (it.children) {
            if (it.children.some((c) => c.id === active || (c.children && (c.children.some((g) => g.id === active))))) {
              next[it.id] = true;
            }
            walk(it.children);
          }
        }
      };
      walk(items);
      return next;
    });
  }, [active, items]);

  // Close drawer on ESC & lock body scroll
  useEffect(() => {
    if (!openDrawer) return;
    const onKey = (e) => { if (e.key === "Escape") setOpenDrawer(false); };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = prev; };
  }, [openDrawer]);

  const activeLabel = (() => {
    const findDeep = (list) => {
      for (const it of list || []) {
        if (it.id === active) return it.label;
        if (it.children) {
          const found = findDeep(it.children);
          if (found) return found;
        }
      }
      return null;
    };
    return findDeep(items) || items[0]?.label || "";
  })();

  const toggle = (id) => setOpen((p) => ({ ...p, [id]: !p[id] }));
  const pick = (id) => { onSelect(id); setOpenDrawer(false); };

  const focusRing =
    "focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/70 focus-visible:ring-offset-2 focus-visible:ring-offset-sidebar";

  const containsActiveDeep = (list) =>
    (list || []).some((c) => c.id === active || (c.children && containsActiveDeep(c.children)));

  const renderLeaf = (it, depth = 0) => {
    const isActive = active === it.id;
    const indent = depth === 0 ? "" : depth === 1 ? "pl-11" : "pl-16 text-[13px]";
    return (
      <button
        key={it.id}
        type="button"
        data-testid={`mnav-${it.id}`}
        onClick={() => pick(it.id)}
        aria-current={isActive ? "page" : undefined}
        className={`w-full flex items-center gap-3 px-4 min-h-[48px] py-2.5 rounded-lg text-[15px] font-medium transition-colors ${focusRing} ${indent} ${
          isActive
            ? "bg-primary/15 text-primary shadow-[inset_0_0_0_1px_hsl(var(--primary)/0.25)]"
            : "text-sidebar-muted hover:text-sidebar-fg hover:bg-sidebar-hover"
        }`}
      >
        {it.icon && <it.icon className={`h-5 w-5 shrink-0 ${isActive ? "text-primary" : ""}`} aria-hidden="true" />}
        <span className="truncate">{it.label}</span>
      </button>
    );
  };

  const renderItem = (it, depth = 0) => {
    if (!it.children) return renderLeaf(it, depth);
    const isOpen = !!open[it.id];
    const containsActive = containsActiveDeep(it.children);
    const groupActive = active === it.id || containsActive;
    const panelId = `mnav-panel-${it.id}`;
    const indent = depth === 0 ? "" : depth === 1 ? "pl-11" : "pl-16 text-[13px]";
    return (
      <div key={it.id} data-testid={`mnav-group-${it.id}`}>
        <button
          type="button"
          onClick={() => {
            toggle(it.id);
            if (it.selectable) pick(it.id);
          }}
          data-testid={`mnav-group-toggle-${it.id}`}
          aria-expanded={isOpen}
          aria-controls={panelId}
          className={`w-full flex items-center gap-3 px-4 min-h-[48px] py-2.5 rounded-lg text-[15px] font-semibold transition-colors ${focusRing} ${indent} ${
            groupActive ? "text-sidebar-fg bg-sidebar-hover" : "text-sidebar-muted hover:text-sidebar-fg hover:bg-sidebar-hover"
          }`}
        >
          {it.icon && <it.icon className={`h-5 w-5 shrink-0 ${groupActive ? "text-primary" : ""}`} aria-hidden="true" />}
          <span className="flex-1 text-left truncate">{it.label}</span>
          <ChevronDown className={`h-4 w-4 shrink-0 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`} aria-hidden="true" />
        </button>
        <div
          id={panelId}
          role="region"
          aria-label={it.label}
          aria-hidden={!isOpen}
          className={`overflow-hidden transition-[max-height,opacity] duration-200 ${
            isOpen ? "max-h-[1200px] opacity-100 mt-0.5" : "max-h-0 opacity-0"
          }`}
        >
          <div className="space-y-0.5 py-0.5" {...(isOpen ? {} : { inert: "" })}>
            {it.children.map((c) => renderItem(c, depth + 1))}
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      {/* Trigger bar (mobile only) */}
      <div className="lg:hidden sticky top-0 z-30 -mx-4 sm:-mx-6 mb-4 px-4 sm:px-6 py-2 bg-background/85 backdrop-blur-md border-b border-border">
        <button
          type="button"
          data-testid="mobile-menu-btn"
          onClick={() => setOpenDrawer(true)}
          aria-label="Menü öffnen"
          aria-expanded={openDrawer}
          className={`w-full flex items-center gap-3 min-h-[48px] px-4 rounded-xl border border-border bg-card text-left transition-colors hover:bg-muted/40 ${focusRing}`}
        >
          <Menu className="h-5 w-5 shrink-0 text-primary" aria-hidden="true" />
          <span className="text-xs uppercase tracking-wider text-muted-foreground">Bereich</span>
          <span className="flex-1 font-semibold text-sm truncate">{activeLabel}</span>
          <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" aria-hidden="true" />
        </button>
      </div>

      {/* Drawer */}
      {openDrawer && (
        <div
          className="lg:hidden fixed inset-0 z-50"
          role="dialog"
          aria-modal="true"
          aria-label="Navigation"
          data-testid="mobile-menu-drawer"
        >
          {/* Backdrop */}
          <button
            type="button"
            aria-label="Menü schließen"
            onClick={() => setOpenDrawer(false)}
            className="absolute inset-0 bg-black/60 backdrop-blur-sm animate-in fade-in duration-150"
            data-testid="mobile-menu-backdrop"
          />
          {/* Panel */}
          <aside
            className="absolute inset-y-0 left-0 w-[86%] max-w-[340px] bg-sidebar text-sidebar-fg flex flex-col shadow-2xl animate-in slide-in-from-left duration-200"
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-sidebar-border">
              <div className="flex items-center gap-2.5">
                <div className="h-14 w-14 rounded-xl bg-white border border-white/10 flex items-center justify-center p-1.5 shrink-0">
                  <img src="/logo.png" alt="Team Phoenix" className="h-full w-full object-contain" />
                </div>
                <span className="font-heading font-bold text-sidebar-fg leading-tight flex flex-col">
                  <span className="text-base tracking-tight">Team Phoenix</span>
                  <span className="text-[10px] font-medium uppercase tracking-[0.14em] text-sidebar-muted -mt-0.5">Team Portal</span>
                </span>
              </div>
              <button
                type="button"
                onClick={() => setOpenDrawer(false)}
                aria-label="Menü schließen"
                data-testid="mobile-menu-close"
                className={`h-11 w-11 flex items-center justify-center rounded-lg text-sidebar-muted hover:text-sidebar-fg hover:bg-sidebar-hover ${focusRing}`}
              >
                <X className="h-5 w-5" aria-hidden="true" />
              </button>
            </div>
            <nav className="flex-1 overflow-y-auto p-3 space-y-1" aria-label="Portal-Bereiche">
              {items.map((it) => renderItem(it, 0))}
            </nav>
          </aside>
        </div>
      )}
    </>
  );
};
